import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next/pages';

import { Button, CopyButton, Flex, Popover, Stack, TextArea } from '@highlight-cp/core-ui';

import { ActionIcons } from '../action-icons';
import type { IconProps } from '../action-icons';
import { markdownToHtml } from '../ai-chat-messages/utils/markdown-to-html';

const SHARED_ICON_PROPS: IconProps = {
  size: 18,
  tooltipPosition: 'bottom',
};

const COPY_FEEDBACK_MS = 2000;

/**
 * Last-resort copy for non-secure contexts / older browsers / restricted iframes
 * where Clipboard APIs are missing or blocked. Uses a throwaway textarea so
 * `document.execCommand('copy')` has a selection.
 */
const copyWithExecCommand = (plain: string): boolean => {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = plain;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.tabIndex = -1;
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus({ preventScroll: true });
  textarea.select();
  textarea.setSelectionRange(0, plain.length);

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const writePlainText = async (plain: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(plain);
      return true;
    }
  } catch {
    // Fall through to execCommand when the Clipboard API is blocked.
  }

  return copyWithExecCommand(plain);
};

/**
 * Writes HTML + plain text so rich paste targets (Docs/Word) get formatting while
 * plain targets still receive markdown. Falls back to plain text on failure.
 */
const writeRichClipboard = async (plain: string, html: string): Promise<boolean> => {
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      return writePlainText(plain);
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
    return true;
  } catch {
    return writePlainText(plain);
  }
};

export interface FeedbackOptionsProps {
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
  /** When provided, clicking thumbs down opens a feedback form. The submitted text is passed to this handler. */
  onThumbsDownFeedback?: (feedback: string) => void;
  /** When provided, clicking try again opens a new generation. The index is passed to this handler. */
  onTryAgain?: (index: number) => void;
  /**
   * When true with `copyValue`, shows a Copy control that writes `copyValue` to the clipboard.
   * Defaults to `false`.
   */
  showCopy?: boolean;
  /** Plain text copied by the Copy control. Required for Copy to appear when `showCopy` is set. */
  copyValue?: string;
  /**
   * Optional HTML fragment written alongside `copyValue` as `text/html`. When omitted but
   * `copyAsHtml` is set, HTML is derived from `copyValue` (markdown) on click.
   */
  copyHtml?: string;
  /**
   * When true with `copyValue`, Copy writes `text/html` (from `copyHtml` or markdown→HTML)
   * plus plain `copyValue`. When false/omitted and `copyHtml` is absent, uses plain CopyButton.
   */
  copyAsHtml?: boolean;
  index?: number;
  /** More controls at the start of the row, before Copy and the feedback icons, such as a download for this answer. */
  leadingActions?: ReactNode;
}

export const FeedbackOptions = ({
  onThumbsUp,
  onThumbsDown,
  onThumbsDownFeedback,
  onTryAgain,
  showCopy = false,
  copyValue,
  copyHtml,
  copyAsHtml = false,
  index = 0,
  leadingActions,
}: FeedbackOptionsProps) => {
  const { t } = useTranslation();
  const [isThumbsUpSelected, setIsThumbsUpSelected] = useState<boolean>(false);
  const [isThumbsDownSelected, setIsThumbsDownSelected] = useState<boolean>(false);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [richCopied, setRichCopied] = useState(false);
  const keepThumbsDownSelectedOnCloseRef = useRef(false);
  const richCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (richCopyTimeoutRef.current != null) {
        clearTimeout(richCopyTimeoutRef.current);
      }
    };
  }, []);

  const handleThumbsUp = useCallback(() => {
    if (isThumbsUpSelected) {
      setIsThumbsUpSelected(false);
      return;
    }

    setIsThumbsUpSelected(true);
    onThumbsUp?.();
  }, [onThumbsUp, isThumbsUpSelected]);

  const closeFeedbackPopover = useCallback(() => {
    setFeedbackPopoverOpen(false);
    setFeedbackText('');
  }, []);

  const deselectThumbsDown = useCallback(() => {
    setIsThumbsDownSelected(false);
    closeFeedbackPopover();
  }, [closeFeedbackPopover]);

  const handlePopoverClose = useCallback(() => {
    if (keepThumbsDownSelectedOnCloseRef.current) {
      keepThumbsDownSelectedOnCloseRef.current = false;
      closeFeedbackPopover();
      return;
    }

    deselectThumbsDown();
  }, [closeFeedbackPopover, deselectThumbsDown]);

  const handleThumbsDown = useCallback(() => {
    if (isThumbsDownSelected) {
      deselectThumbsDown();
      return;
    }

    onThumbsDown?.();
    setIsThumbsDownSelected(true);
    if (onThumbsDownFeedback) {
      setFeedbackPopoverOpen(true);
    }
  }, [onThumbsDown, onThumbsDownFeedback, isThumbsDownSelected, deselectThumbsDown]);

  const handleFeedbackSubmit = useCallback(() => {
    const trimmedFeedback = feedbackText.trim();
    if (!trimmedFeedback) {
      return;
    }

    onThumbsDownFeedback?.(trimmedFeedback);
    keepThumbsDownSelectedOnCloseRef.current = true;
    closeFeedbackPopover();
  }, [onThumbsDownFeedback, feedbackText, closeFeedbackPopover]);

  const handleRichCopy = useCallback(async () => {
    if (!copyValue) {
      return;
    }

    let html = copyHtml?.trim() || '';
    if (!html) {
      try {
        html = markdownToHtml(copyValue);
      } catch {
        html = '';
      }
    }

    const wrote = html
      ? await writeRichClipboard(copyValue, html)
      : await writePlainText(copyValue);

    if (!wrote) {
      return;
    }

    setRichCopied(true);
    if (richCopyTimeoutRef.current != null) {
      clearTimeout(richCopyTimeoutRef.current);
    }
    richCopyTimeoutRef.current = setTimeout(() => {
      setRichCopied(false);
      richCopyTimeoutRef.current = null;
    }, COPY_FEEDBACK_MS);
  }, [copyValue, copyHtml]);

  const feedbackLabel = t(`What don't you like about this?`);
  const hasFeedback = feedbackText.trim().length > 0;
  const shouldShowCopy = showCopy && Boolean(copyValue);
  const useRichCopy = Boolean(copyHtml?.trim()) || copyAsHtml;

  const thumbsDownIcon = (
    <ActionIcons.ThumbsDown
      {...SHARED_ICON_PROPS}
      variant={isThumbsDownSelected ? 'light' : undefined}
      tooltip={t(`I didn't like this!`)}
      onClick={handleThumbsDown}
    />
  );

  const copyControl =
    shouldShowCopy && copyValue ? (
      useRichCopy ? (
        <ActionIcons.Copy
          {...SHARED_ICON_PROPS}
          ariaLabel={richCopied ? t('Copied') : t('Copy')}
          tooltip={richCopied ? t('Copied') : t('Copy')}
          variant={richCopied ? 'light' : undefined}
          onClick={() => {
            void handleRichCopy();
          }}
        />
      ) : (
        <CopyButton value={copyValue} timeout={COPY_FEEDBACK_MS}>
          {({ copied, copy }) => (
            <ActionIcons.Copy
              {...SHARED_ICON_PROPS}
              ariaLabel={copied ? t('Copied') : t('Copy')}
              tooltip={copied ? t('Copied') : t('Copy')}
              variant={copied ? 'light' : undefined}
              onClick={() => {
                copy();
              }}
            />
          )}
        </CopyButton>
      )
    ) : null;

  return (
    <Flex gap={2}>
      {leadingActions}
      {copyControl}
      {!isThumbsDownSelected && onThumbsUp != null && (
        <ActionIcons.ThumbsUp
          {...SHARED_ICON_PROPS}
          variant={isThumbsUpSelected ? 'light' : undefined}
          tooltip={t('I like this!')}
          onClick={handleThumbsUp}
        />
      )}
      {!isThumbsUpSelected &&
        onThumbsDown != null &&
        (onThumbsDownFeedback ? (
          <Popover
            opened={feedbackPopoverOpen}
            onDismiss={handlePopoverClose}
            position="bottom-start"
            withArrow
          >
            <Popover.Target>{thumbsDownIcon}</Popover.Target>
            <Popover.Dropdown>
              <Stack gap="xs">
                <TextArea
                  aria-label={feedbackLabel}
                  placeholder={feedbackLabel}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.currentTarget.value)}
                  autosize
                  minRows={3}
                  maxRows={6}
                />
                <Flex justify="flex-end" gap="xs">
                  <Button variant="default" size="xs" onClick={deselectThumbsDown}>
                    {t('Cancel')}
                  </Button>
                  <Button size="xs" disabled={!hasFeedback} onClick={handleFeedbackSubmit}>
                    {t('Submit')}
                  </Button>
                </Flex>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        ) : (
          thumbsDownIcon
        ))}
      {onTryAgain && (
        <ActionIcons.TryAgain
          {...SHARED_ICON_PROPS}
          tooltip={t('Try again')}
          onClick={() => onTryAgain?.(index)}
        />
      )}
    </Flex>
  );
};
